<template>
  <v-container class="pa-0" fluid>
    <v-card>
      <v-card-text>
        <v-container fluid class="pa-0">
          <v-row class="pt-3 pb-0">
            <v-col cols="8" class="py-0 pl-3">
              <span class="table-card-title pl-0">Saldos de Pontos bloqueados</span> <br />
              <p class="subtitle pt-1 description-text">
                Quando um consumidor com pontos ativados se identifica no PDV, seu saldo de pontos é bloqueado durante a transação para impedir que
                movimentações de saldo aconteçam durante a compra. No entanto, devido a eventuais falhas de comunicação entre o PDV e a Mercafacil, o saldo pode
                continuar bloqueado mesmo após a compra ter sido confirmada ou cancelada no PDV. Aqui você pode remover o bloqueio de saldos de pontos de um
                consumidor da sua rede. Abaixo você confere os bloqueios que você tem.
              </p>
            </v-col>
            <v-col cols="4" class="py-0">
              <v-row class="pr-3">
                <v-col class="search-and-export-header" cols="12">
                  <div>
                    <mf-search
                      v-model="search"
                      placeholder="Busque por CPF/CNPJ"
                      hide-details
                      :disabled="!hasPiiAccess"
                      :tooltipMessage="!hasPiiAccess ? 'Você precisa ter a permissão de acesso a informações privilegiadas para acessar esse recurso' : ''"
                    />
                  </div>
                </v-col>
              </v-row>
            </v-col>
          </v-row>
        </v-container>
        <paginated-data-table
          ref="table"
          :headers="headers"
          :search="search"
          :query="query"
          :custom-items-per-page="10"
          :queryName="'pointsUserLocks'"
          :queryMainPropertyName="'items'"
          :queryVariables="queryVariables"
          default-table-sort-by="locked_at"
          :default-table-sort-desc="true"
          @update:maxRecords="updateMaxRecords"
        >
          <template v-slot:item.consumer_document_no="{ item }">
            <v-tooltip max-width="250" top content-class="truncated-tooltip" :disabled="hasPiiAccess">
              <template v-slot:activator="{ on }">
                <span v-on="on">
                  <template v-if="hasPiiAccess"> {{ getSafe(() => item.consumer_document_no) | cpfCnpj }}</template>
                  <template v-else>
                    {{ item.consumer_document_no }}
                  </template>
                </span>
              </template>
              <span>Você precisa ter acessos a dados sensíveis para poder ver essa informação.</span>
            </v-tooltip>
          </template>
          <template v-slot:item.consumer_name="{ item }">
            <v-tooltip max-width="250" top content-class="truncated-tooltip" :disabled="hasPiiAccess">
              <template v-slot:activator="{ on }">
                <span v-on="on">
                  <template>
                    {{ item.consumer_name }}
                  </template>
                </span>
              </template>
              <span>Você precisa ter acessos a dados sensíveis para poder ver essa informação.</span>
            </v-tooltip>
          </template>
          <template v-slot:item.transaction_id="{ item }">
            {{ item.transaction_id || '-' }}
          </template>
          <template v-slot:item.locked_value="{ item }"> {{ item.locked_value ? item.locked_value : '' }} pontos </template>
          <template v-slot:item.actions="{ item }">
            <span class="link" @click="confirmUnlockPoints(item.consumer_document_no)">Desbloquear</span>
          </template>
        </paginated-data-table>
      </v-card-text>
    </v-card>
  </v-container>
</template>

<script>
import PaginatedDataTable from '@/components/PaginatedDataTable'
import { QUERY_GET_POINTS_USER_LOCKS, MUTATION_RELEASE_POINTS_USER_LOCK } from '@/modules/mobile/pontos/graphql'
import { DateTime } from 'luxon'
import cpfCnpj from '@/filters/cpfCnpj.js'

export default {
  name: 'PointsBalanceLocksTable',
  components: {
    PaginatedDataTable,
  },
  data: () => ({
    search: '',
    maxRecords: 0,
  }),
  computed: {
    DateTime() {
      return DateTime
    },
    query() {
      return QUERY_GET_POINTS_USER_LOCKS
    },
    queryVariables() {
      return {
        search: this.search,
      }
    },
    headers() {
      return [
        {
          text: 'CPF/CNPJ',
          value: 'consumer_document_no',
          width: '15%',
          sortable: false,
        },
        {
          text: 'Nome',
          value: 'consumer_name',
          width: '25%',
          sortable: false,
        },
        {
          text: 'ID da transação',
          value: 'transaction_id',
          width: '25%',
        },
        {
          text: 'Pontos',
          value: 'locked_value',
          width: '15%',
        },
        {
          text: '',
          value: 'actions',
          width: '10%',
          sortable: false,
        },
      ]
    },
    hasPiiAccess() {
      return this.$hasPiiAccess()
    },
  },
  methods: {
    reload() {
      this.$refs.table.waitAndGetData()
    },
    updateMaxRecords(records) {
      this.maxRecords = records
    },
    confirmUnlockPoints(documentNo) {
      this.$confirm({
        title: 'Desbloquear saldo',
        message: `Tem certeza que deseja desbloquear o saldo de pontos do cliente com o CPF ${cpfCnpj(
          documentNo
        )}? Ao confirmar essa ação, os pontos deste cliente serão redisponibilizados.`,
        confirmText: 'Desbloquear',
        confirmColor: 'vermelho-d20',
        cancelText: 'Cancelar',
      })
        .then(() => {
          this.unblockPoints(documentNo)
        })
        .catch(() => {})
    },
    unblockPoints(documentNo) {
      this.$apollo
        .mutate({
          mutation: MUTATION_RELEASE_POINTS_USER_LOCK,
          variables: {
            documentNo: documentNo,
          },
        })
        .then(() => {
          this.$snackbar({ message: 'Saldo desbloqueado com sucesso', snackbarColor: 'success' })
          this.reload()
        })
        .catch(() => {
          this.$snackbar({ message: 'Erro ao desbloquear saldo', snackbarColor: 'error' })
        })
    },
  },
}
</script>

<style lang="scss" scoped>
.description-text {
  word-break: break-word;
}

.search-and-export-header {
  display: flex !important;
  justify-content: flex-end;
}

.search-and-export-header > span:first-child {
  align-self: center;
  margin: 0 8px 0 8px;
}

.search-and-export-header > div {
  width: 100%;
  max-width: 300px;
  min-width: 110px;
}

.link {
  color: $marca-0-l40 !important;
  text-decoration: underline;
  cursor: pointer;
}

.link:hover {
  text-decoration: underline;
}
</style>
